import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Camera, ArrowClockwise, X } from "@phosphor-icons/react";

export default function CameraCapture({ onCapture, initialPhoto }) {
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const streamRef = useRef(null);
    const [active, setActive] = useState(false);
    const [preview, setPreview] = useState(initialPhoto || null);
    const [error, setError] = useState(null);

    const start = async () => {
        setError(null);
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: "environment", width: { ideal: 640 }, height: { ideal: 480 } },
                audio: false,
            });
            streamRef.current = stream;
            if (videoRef.current) {
                videoRef.current.srcObject = stream;
                await videoRef.current.play();
            }
            setActive(true);
        } catch (e) {
            setError("Camera access denied or unavailable.");
        }
    };

    const stop = () => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach((t) => t.stop());
            streamRef.current = null;
        }
        setActive(false);
    };

    useEffect(() => () => stop(), []);

    const capture = () => {
        const video = videoRef.current;
        const canvas = canvasRef.current;
        if (!video || !canvas) return;
        canvas.width = video.videoWidth || 640;
        canvas.height = video.videoHeight || 480;
        canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);
        const dataUrl = canvas.toDataURL("image/jpeg", 0.85);
        setPreview(dataUrl);
        stop();
        onCapture?.(dataUrl);
    };

    const retake = () => {
        setPreview(null);
        onCapture?.(null);
        start();
    };

    return (
        <div className="w-full">
            <div className="relative w-full aspect-[4/3] bg-zinc-900 rounded-md overflow-hidden border border-border">
                {preview ? (
                    <img src={preview} alt="Visitor" className="w-full h-full object-cover" data-testid="camera-preview" />
                ) : active ? (
                    <video
                        ref={videoRef}
                        playsInline
                        muted
                        className="w-full h-full object-cover"
                        data-testid="camera-video"
                    />
                ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center text-white/70 gap-2 p-6 text-center">
                        <Camera size={40} weight="duotone" />
                        <span className="text-sm">Tap to capture visitor photo</span>
                        {error && <span className="text-xs text-red-400">{error}</span>}
                    </div>
                )}
                <canvas ref={canvasRef} className="hidden" />
            </div>
            <div className="mt-3 flex gap-2">
                {!active && !preview && (
                    <Button
                        type="button"
                        onClick={start}
                        data-testid="btn-camera-start"
                        className="flex-1 bg-brand hover:bg-brand/90 text-white h-11"
                    >
                        <Camera className="mr-2" size={18} /> Start Camera
                    </Button>
                )}
                {active && (
                    <>
                        <Button
                            type="button"
                            onClick={capture}
                            data-testid="btn-camera-capture"
                            className="flex-1 bg-brand hover:bg-brand/90 text-white h-11"
                        >
                            Capture
                        </Button>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={stop}
                            data-testid="btn-camera-cancel"
                            className="h-11"
                        >
                            <X size={18} />
                        </Button>
                    </>
                )}
                {preview && (
                    <Button
                        type="button"
                        variant="outline"
                        onClick={retake}
                        data-testid="btn-camera-retake"
                        className="flex-1 h-11"
                    >
                        <ArrowClockwise className="mr-2" size={18} /> Retake
                    </Button>
                )}
            </div>
        </div>
    );
}
